import { DoctorLayout } from "@/components/layout/doctor-layout";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Download,
  Trash2,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  User,
  Calendar,
  ShieldCheck,
  BookOpen,
  FileText,
  GraduationCap,
  ClipboardList,
  Ban,
  PauseCircle,
} from "lucide-react";
import { RegistrationVerificationPanel } from "@/components/ui/registration-verification-panel";
import { BrowserStorageCard } from "@/components/ui/browser-storage-card";
import { TeamCodeCard } from "@/components/settings/team-code-card";

interface DataSummary {
  conversations: number;
  messages: number;
  aiAuditLogs: number;
  lastAiInteraction: string | null;
  doctorCpdActivities: number;
  doctorCurriculumEntries: number;
}

interface DeleteResult {
  success: boolean;
  deleted: {
    conversations: number;
    messages: number;
    aiAuditLogs: number;
    doctorCpdActivities: number;
    doctorCurriculumEntries: number;
  };
  deletedAt: string;
  confirmationEmailSent?: boolean;
}

const BASE_URL = import.meta.env.BASE_URL.replace(/\/$/, "");

function StatTile({ value, label, icon }: { value: number | string; label: string; icon: React.ReactNode }) {
  return (
    <div className="bg-slate-50 rounded-xl p-4 flex items-start gap-3">
      <div className="mt-0.5 text-slate-400 shrink-0">{icon}</div>
      <div>
        <p className="text-2xl font-bold text-slate-900 leading-none">{value}</p>
        <p className="text-sm text-slate-500 mt-1">{label}</p>
      </div>
    </div>
  );
}

export default function DoctorSettings() {
  const [summary, setSummary] = useState<DataSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteResult, setDeleteResult] = useState<DeleteResult | null>(null);

  const [exporting, setExporting] = useState<null | "json" | "csv">(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const [appraisalDate, setAppraisalDate] = useState(
    localStorage.getItem("rheumhub_doctor_appraisal_date") ?? ""
  );
  const [appraisalSaved, setAppraisalSaved] = useState(false);

  const [revalidationDate, setRevalidationDate] = useState(
    localStorage.getItem("rheumhub_doctor_revalidation_date") ?? ""
  );
  const [revalidationSaved, setRevalidationSaved] = useState(false);

  useEffect(() => {
    fetch(`${BASE_URL}/api/user/data/summary`)
      .then((r) => r.json())
      .then((data) => { setSummary(data); setSummaryLoading(false); })
      .catch(() => { setSummaryError("Could not load your data summary."); setSummaryLoading(false); });
  }, []);

  const handleSaveAppraisal = () => {
    localStorage.setItem("rheumhub_doctor_appraisal_date", appraisalDate);
    setAppraisalSaved(true);
    setTimeout(() => setAppraisalSaved(false), 2000);
  };

  const handleSaveRevalidation = () => {
    localStorage.setItem("rheumhub_doctor_revalidation_date", revalidationDate);
    setRevalidationSaved(true);
    setTimeout(() => setRevalidationSaved(false), 2000);
  };

  const handleExport = async (format: "json" | "csv") => {
    setExporting(format);
    setExportError(null);
    try {
      const response = await fetch(`${BASE_URL}/api/user/data/export?format=${format}`);
      if (!response.ok) {
        setExportError(
          response.status === 429
            ? "Too many export requests right now — please wait a few minutes and try again."
            : "We couldn't prepare your export. Please try again shortly.",
        );
        return;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rheumhub-doctor-data-export-${Date.now()}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setExportError("We couldn't prepare your export. Please try again shortly.");
    } finally {
      setExporting(null);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const response = await fetch(`${BASE_URL}/api/user/data`, { method: "DELETE" });
      const result: DeleteResult = await response.json();
      setDeleteResult(result);
      setSummary({
        conversations: 0,
        messages: 0,
        aiAuditLogs: 0,
        lastAiInteraction: null,
        doctorCpdActivities: 0,
        doctorCurriculumEntries: 0,
      });
      setDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <DoctorLayout title="Your Data & Settings">
      <div className="max-w-2xl mx-auto space-y-8">

        <div className="flex items-start gap-4">
          <div className="bg-indigo-50 p-3 rounded-2xl">
            <User className="h-7 w-7 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">Your Data & Privacy</h2>
            <p className="text-slate-500 text-sm mt-0.5">
              Manage your data as required under UK GDPR. You have the right to access, export, and erase your data — including your CPD activities and curriculum progress.
            </p>
          </div>
        </div>

        {/* Professional verification */}
        <section className="bg-white rounded-2xl border border-slate-200 p-8 space-y-4">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-indigo-600" />
            <h3 className="font-bold text-slate-900">Professional verification</h3>
          </div>
          <RegistrationVerificationPanel userRole="doctor" />
        </section>

        <TeamCodeCard accent="indigo" />

        {/* Appraisal & Revalidation Dates */}
        <section className="bg-white rounded-2xl border border-slate-200 p-8 space-y-6">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-indigo-600" />
            <h3 className="font-bold text-slate-900">Appraisal &amp; Revalidation dates</h3>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-700">Next annual appraisal</p>
            <p className="text-sm text-slate-600">
              Set your next GMC revalidation appraisal date. Displayed as a countdown on your dashboard and revalidation tracker.
            </p>
            <div className="flex gap-3 items-center">
              <input
                type="date"
                value={appraisalDate}
                onChange={(e) => setAppraisalDate(e.target.value)}
                className="border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-300"
              />
              <Button
                onClick={handleSaveAppraisal}
                disabled={!appraisalDate}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                {appraisalSaved ? <CheckCircle2 className="h-4 w-4 mr-2" /> : null}
                {appraisalSaved ? "Saved" : "Save date"}
              </Button>
            </div>
          </div>

          <div className="border-t border-slate-100 pt-5 space-y-2">
            <p className="text-sm font-medium text-slate-700">GMC revalidation date (5-year cycle)</p>
            <p className="text-sm text-slate-600">
              Set your GMC revalidation due date — the end of your current 5-year cycle. This appears as a months-remaining countdown on the Revalidation Dashboard.
            </p>
            <div className="flex gap-3 items-center">
              <input
                type="date"
                value={revalidationDate}
                onChange={(e) => setRevalidationDate(e.target.value)}
                className="border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-300"
              />
              <Button
                onClick={handleSaveRevalidation}
                disabled={!revalidationDate}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                {revalidationSaved ? <CheckCircle2 className="h-4 w-4 mr-2" /> : null}
                {revalidationSaved ? "Saved" : "Save date"}
              </Button>
            </div>
          </div>

          <p className="text-xs text-slate-400">
            Dates are saved locally on this device only. Clear browser data to remove.
          </p>
        </section>

        {/* Data summary */}
        <section className="bg-white rounded-2xl border border-slate-200 p-8 space-y-4">
          <h3 className="font-bold text-slate-900">Data summary</h3>

          {summaryLoading ? (
            <div className="flex items-center gap-2 text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : summaryError ? (
            <p className="text-red-600 text-sm">{summaryError}</p>
          ) : summary ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <StatTile value={summary.conversations} label="AI conversations" icon={<BookOpen className="h-4 w-4" />} />
                <StatTile value={summary.messages} label="Messages sent" icon={<FileText className="h-4 w-4" />} />
                <StatTile value={summary.doctorCpdActivities} label="CPD activities logged" icon={<GraduationCap className="h-4 w-4" />} />
                <StatTile value={summary.doctorCurriculumEntries} label="Curriculum competencies tracked" icon={<ClipboardList className="h-4 w-4" />} />
                <StatTile value={summary.aiAuditLogs} label="AI interactions logged" icon={<ShieldCheck className="h-4 w-4" />} />
              </div>

              {summary.lastAiInteraction && (
                <p className="text-xs text-slate-500 pt-1">
                  Last AI interaction:{" "}
                  <span className="font-medium text-slate-700">
                    {new Date(summary.lastAiInteraction).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </span>
                </p>
              )}
            </>
          ) : null}

          <p className="text-xs text-slate-400 border-t border-slate-100 pt-3">
            Doctor data (conversations, CPD activities, curriculum records) is retained for up to 365 days after last activity, then automatically deleted.
          </p>
        </section>

        {/* Export */}
        <section className="bg-white rounded-2xl border border-slate-200 p-8 space-y-4">
          <h3 className="font-bold text-slate-900">Export your data</h3>
          <p className="text-sm text-slate-600">
            Download a complete copy of all your data — as JSON (full nested structure) or CSV (spreadsheet-friendly) — including your AI conversations, CPD activities, and curriculum competency records. This fulfils your right to <strong>data portability</strong> under UK GDPR Article 20.
          </p>
          <div className="bg-slate-50 rounded-xl p-4 text-xs text-slate-500 space-y-1">
            <p className="font-semibold text-slate-700 mb-2">Your export includes:</p>
            <p>✓ All AI conversations and messages</p>
            <p>✓ AI audit log metadata (model, timestamps)</p>
            <p>✓ CPD activities (title, date, category, credit hours, reflection notes)</p>
            <p>✓ Curriculum competency entries and evidence notes</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => handleExport("json")}
              disabled={exporting !== null}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {exporting === "json" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
              Export as JSON
            </Button>
            <Button
              onClick={() => handleExport("csv")}
              disabled={exporting !== null}
              variant="outline"
              className="border-indigo-600 text-indigo-600 hover:bg-indigo-50"
            >
              {exporting === "csv" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
              Export as CSV
            </Button>
          </div>
          <p className="text-xs text-slate-500">
            JSON preserves the full nested structure. CSV provides a spreadsheet-friendly format with one section per dataset.
          </p>
          {exportError && (
            <p className="text-sm text-red-600">{exportError}</p>
          )}
        </section>

        {/* Delete */}
        <section className="bg-white rounded-2xl border border-red-200 p-8 space-y-4">
          <h3 className="font-bold text-slate-900 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            Delete all my data
          </h3>
          <p className="text-sm text-slate-600">
            This permanently deletes all your conversations, messages, CPD activities, curriculum entries, and AI audit logs. This action <strong>cannot be undone</strong>. Your account will remain active.
          </p>

          {deleteResult ? (
            <div className="flex items-start gap-3 bg-teal-50 border border-teal-200 rounded-xl p-4 text-sm text-teal-800">
              <CheckCircle2 className="h-5 w-5 text-teal-500 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold mb-1">Data deleted successfully</p>
                <p className="text-xs text-teal-700">
                  Removed {deleteResult.deleted.conversations} conversation{deleteResult.deleted.conversations !== 1 ? "s" : ""},{" "}
                  {deleteResult.deleted.messages} message{deleteResult.deleted.messages !== 1 ? "s" : ""},{" "}
                  {deleteResult.deleted.doctorCpdActivities} CPD {deleteResult.deleted.doctorCpdActivities !== 1 ? "activities" : "activity"},{" "}
                  {deleteResult.deleted.doctorCurriculumEntries} curriculum {deleteResult.deleted.doctorCurriculumEntries !== 1 ? "entries" : "entry"},{" "}
                  and {deleteResult.deleted.aiAuditLogs} audit log {deleteResult.deleted.aiAuditLogs !== 1 ? "entries" : "entry"}.
                </p>
                {deleteResult.confirmationEmailSent && (
                  <p className="text-xs text-teal-700 mt-1.5">
                    A confirmation email has been sent to your registered address for your records.
                  </p>
                )}
              </div>
            </div>
          ) : deleteConfirm ? (
            <div className="space-y-3">
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-800">
                <p className="font-semibold">Are you sure?</p>
                <p className="mt-1 text-xs text-red-700">
                  This will permanently erase all your conversations, CPD activities, curriculum entries, and AI audit logs. This cannot be undone.
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                  Yes, delete everything
                </Button>
                <Button variant="outline" onClick={() => setDeleteConfirm(false)} disabled={deleting}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="outline"
              onClick={() => setDeleteConfirm(true)}
              className="border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete all my data
            </Button>
          )}
        </section>

        {/* Right to Object */}
        <section className="bg-white rounded-2xl border border-slate-200 p-8 space-y-4">
          <div className="flex items-center gap-2">
            <Ban className="h-5 w-5 text-slate-500" />
            <h3 className="font-bold text-slate-900">Right to Object (Article 21 UK GDPR)</h3>
          </div>
          <p className="text-sm text-slate-600">
            Where RheumHub processes your data based on <strong>legitimate interests</strong> (Article 6(1)(f)), you have the right to object at any time on grounds relating to your particular situation. We will stop processing unless we can demonstrate compelling legitimate grounds that override your interests, rights, and freedoms.
          </p>
          <p className="text-sm text-slate-600">To exercise this right, email us with a brief explanation of your situation:</p>
          <a
            href="mailto:privacy@medantixhealth.co.uk?subject=Right%20to%20Object%20-%20Article%2021%20UK%20GDPR"
            className="inline-flex items-center gap-2 text-sm font-medium text-indigo-700 underline hover:text-indigo-800"
          >
            privacy@medantixhealth.co.uk
          </a>
        </section>

        {/* Right to Restrict Processing */}
        <section className="bg-white rounded-2xl border border-slate-200 p-8 space-y-4">
          <div className="flex items-center gap-2">
            <PauseCircle className="h-5 w-5 text-slate-500" />
            <h3 className="font-bold text-slate-900">Right to Restrict Processing (Article 18 UK GDPR)</h3>
          </div>
          <p className="text-sm text-slate-600">
            You may ask us to restrict processing of your personal data in certain circumstances — for example, if you contest the accuracy of your data or if processing is unlawful and you prefer restriction over erasure.
          </p>
          <p className="text-sm text-slate-600">To request a restriction:</p>
          <a
            href="mailto:privacy@medantixhealth.co.uk?subject=Right%20to%20Restrict%20Processing%20-%20Article%2018%20UK%20GDPR"
            className="inline-flex items-center gap-2 text-sm font-medium text-indigo-700 underline hover:text-indigo-800"
          >
            privacy@medantixhealth.co.uk
          </a>
        </section>

        <BrowserStorageCard />

        <p className="text-center text-xs text-slate-400">
          For questions about your data, contact <strong>privacy@medantixhealth.co.uk</strong>.{" "}
          <a href="/privacy" className="underline hover:text-slate-600">Privacy Policy</a>
        </p>

      </div>
    </DoctorLayout>
  );
}
